import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext/AuthContext';

export type FundedCtaKind = 'STORE' | 'CHOOSE_ACCOUNT' | 'FOLLOW_APPLICATION' | 'VIEW_APPLICATION' | 'APPLY';

export interface FundedCta {
  kind: FundedCtaKind;
  /** Route to navigate to */
  to: '/store' | '/buy-funded' | '/apply';
  /** Button label */
  label: string;
  /** One-line explanation of where the user stands, for empty states */
  hint: string;
}

/**
 * Single source of truth for the "get your funded account" call to action.
 * Since the application-based model, a user without an instant funded account is
 * sent to the application funnel (or to its status) rather than to the store.
 */
export const useFundedCta = (): FundedCta => {
  const { t } = useTranslation('application');
  const { profile } = useAuth();
  const status = profile?.fundedApplicationStatus ?? null;

  let kind: FundedCtaKind;
  if (profile?.hasActiveInstantFunded) {
    kind = 'STORE';
  } else if (status === 'ACCEPTED' || profile?.instantFundedEligible || profile?.hasFirstTimeDeposit) {
    kind = 'CHOOSE_ACCOUNT';
  } else if (status === 'SUBMITTED') {
    kind = 'FOLLOW_APPLICATION';
  } else if (status === 'REJECTED') {
    kind = 'VIEW_APPLICATION';
  } else {
    kind = 'APPLY';
  }

  const to: FundedCta['to'] =
    kind === 'STORE' ? '/store' : kind === 'CHOOSE_ACCOUNT' ? '/buy-funded' : '/apply';

  return {
    kind,
    to,
    label: t(`CTA.${kind}.LABEL`),
    hint: t(`CTA.${kind}.HINT`),
  };
};

export default useFundedCta;
